import type { QueryClient } from '@tanstack/react-query';

import { cloneLensCatalog } from './scenarioLensCatalog.helpers';
import type { LensCatalogData, LensCatalogFetchResult } from './scenarioLensCatalog.types';

export const PPA_LENS_SCENARIO_CONFIG_QUERY_KEY = 'ppa-lens-scenario-config' as const;

export const getLensScenarioConfigQueryKey = (lensId: number, scenarioId: number) =>
    [PPA_LENS_SCENARIO_CONFIG_QUERY_KEY, lensId, scenarioId] as const;

export const getCachedLensConfigResult = (
    lensId: number | null,
    scenarioId: number | null,
    queryClient: QueryClient,
): LensCatalogFetchResult | undefined => {
    if (lensId == null || scenarioId == null) {
        return undefined;
    }

    return queryClient.getQueryData<LensCatalogFetchResult>(getLensScenarioConfigQueryKey(lensId, scenarioId));
};

export const hasCachedLensCatalog = (
    lensId: number | null,
    scenarioId: number | null,
    queryClient: QueryClient,
): boolean => getCachedLensConfigResult(lensId, scenarioId, queryClient)?.catalog != null;

export const clearScenarioLensConfigCache = (scenarioId: number | null, queryClient: QueryClient): void => {
    if (scenarioId == null) {
        return;
    }

    queryClient.removeQueries({
        predicate: (query) =>
            query.queryKey[0] === PPA_LENS_SCENARIO_CONFIG_QUERY_KEY && query.queryKey[2] === scenarioId,
    });
};

/** Resolves catalog for a lens: local draft beats in-flight query data beats React Query cache. */
export const resolveLensCatalog = (
    lensId: number | null,
    scenarioId: number | null,
    catalogDraftsByLensId: Record<number, LensCatalogData>,
    queryClient: QueryClient,
    queryResult?: LensCatalogFetchResult | null,
): LensCatalogData | null => {
    if (lensId == null) {
        return null;
    }

    const localDraft = catalogDraftsByLensId[lensId];
    if (localDraft) {
        return localDraft;
    }

    if (queryResult?.lensId === lensId && queryResult.catalog) {
        return cloneLensCatalog(queryResult.catalog);
    }

    const cached = getCachedLensConfigResult(lensId, scenarioId, queryClient);
    if (cached?.catalog) {
        return cloneLensCatalog(cached.catalog);
    }

    return null;
};

export const hasResolvedLensCatalog = (
    lensId: number | null,
    scenarioId: number | null,
    catalogDraftsByLensId: Record<number, LensCatalogData>,
    queryClient: QueryClient,
    queryResult?: LensCatalogFetchResult | null,
): boolean =>
    resolveLensCatalog(lensId, scenarioId, catalogDraftsByLensId, queryClient, queryResult) != null;
